import { ChordLib, getDiatonicFunction, getReharmOptions } from '@tunasong/music-lib'
import type { Chord, Substitution } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import type { ActiveSubstitution } from './reharm-progression-strip.js'
import { ReharmSubstitutionCard } from './reharm-substitution-card.js'
import styles from './reharm-side-panel.module.css'

export interface ReharmSidePanelProps {
  selectedSlotIndex: number | null
  progression: readonly Chord[]
  keyScale: Scale
  activeSubstitution: ActiveSubstitution | null
  onApply: (slotIndex: number, substitution: Substitution) => void
}

/**
 * Right-hand panel that shows the 5 substitution-technique cards for the
 * currently-selected slot. When no slot is selected, shows a brief hint.
 * When all 5 techniques are unavailable at the selected slot, renders
 * Bowie's `subst-none-apply` empty-state copy above the disabled cards.
 */
export const ReharmSidePanel: FC<ReharmSidePanelProps> = props => {
  const { selectedSlotIndex, progression, keyScale, activeSubstitution, onApply } = props
  const emptyCopy = useTheoryCopy('subst-none-apply')

  if (selectedSlotIndex === null) {
    return (
      <aside className={styles.panel}>
        <p className={styles.hint}>Click a chord to see substitutions.</p>
      </aside>
    )
  }

  const slotChord = progression[selectedSlotIndex]
  const roman = getDiatonicFunction(slotChord, keyScale) ?? '?'
  const options = getReharmOptions(selectedSlotIndex, progression, keyScale)
  const anyApplicable = options.some(o => o.available)

  return (
    <aside className={styles.panel} aria-label={`Substitutions for slot ${selectedSlotIndex + 1}`}>
      <header className={styles.header}>
        <h2 className={styles.title}>
          Substitutions for slot {selectedSlotIndex + 1}: {ChordLib.getName(slotChord)} ({roman})
        </h2>
      </header>
      {!anyApplicable && emptyCopy?.body ? <p className={styles.empty}>{emptyCopy.body}</p> : null}
      <ul className={styles.list}>
        {options.map(option => (
          <li key={option.technique} className={styles.listItem}>
            <ReharmSubstitutionCard
              substitution={option}
              isActive={
                activeSubstitution !== null &&
                activeSubstitution.slotIndex === selectedSlotIndex &&
                activeSubstitution.substitution.technique === option.technique
              }
              onApply={() => onApply(selectedSlotIndex, option)}
            />
          </li>
        ))}
      </ul>
    </aside>
  )
}
